import React from 'react';
import '../styles/PrivacyPolicy.css';

const PrivacyPolicy = () => {

  return (
    <div className="privacy-policy-container">
      <div className="privacy-policy-content">
        <div className="privacy-header">
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last Updated: 21 September 2026</p>
        </div>

        <div className="privacy-sections">
          {/* Introduction */}
          <section className="privacy-section">
            <h2>1. Introduction</h2>
            <p>
              Welcome to Doctaura Health Management System. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services.
            </p>
            <p>
              We are committed to protecting your privacy and ensuring you have a positive experience on our platform. This policy applies to all information collected through our platform and services.
            </p>
            <p>
              <strong>Geographical Scope:</strong> Doctaura Health Management System is designed for use in Mauritius only. This platform is intended for healthcare professionals and patients located in Mauritius. If you are accessing this system from outside Mauritius, please be aware that this service may not be available in your location or may not comply with local regulations in your jurisdiction.
            </p>
          </section>

          {/* What Information We Collect */}
          <section className="privacy-section">
            <h2>2. What Information Do We Collect?</h2>
            
            <h3>2.1 Information You Provide Directly</h3>
            <ul>
              <li><strong>Account Registration:</strong> Name, email address, phone number, professional credentials (for doctors), specialty, and account password</li>
              <li><strong>Profile Information:</strong> Profile picture, biography, clinic/hospital information, qualifications, and experience</li>
              <li><strong>Medical Records:</strong> Patient medical history, diagnoses, treatment plans, medications, and consultation notes (for authorized users)</li>
              <li><strong>Appointment Information:</strong> Appointment scheduling details, appointment notes, and consultation summaries</li>
              <li><strong>Payment Information:</strong> Billing address, payment method details (processed securely through third-party providers)</li>
              <li><strong>Communication:</strong> Messages, feedback, support requests, and inquiries sent through the platform</li>
            </ul>

            <h3>2.2 Information Collected Automatically</h3>
            <ul>
              <li><strong>Usage Information:</strong> Pages visited, features accessed, time spent on the platform, search queries</li>
              <li><strong>Device Information:</strong> Browser type, operating system, IP address, device type, and unique device identifiers</li>
              <li><strong>Technical Information:</strong> Log data, crash reports, and performance metrics</li>
              <li><strong>Location Information:</strong> Approximate location based on IP address (if enabled)</li>
            </ul>

            <h3>2.3 Information from Third Parties</h3>
            <ul>
              <li>Information from identity verification services</li>
              <li>Payment processor information</li>
              <li>Analytics providers</li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section className="privacy-section">
            <h2>3. How We Process and Use Your Information</h2>
            <p>We process your information for the following purposes:</p>
            
            <h3>3.1 Service Delivery</h3>
            <ul>
              <li>Providing healthcare consultations and appointment scheduling services</li>
              <li>Managing patient-doctor interactions and medical records</li>
              <li>Processing payments and subscription management</li>
              <li>Sending appointment reminders and healthcare notifications</li>
              <li>Delivering technical support and resolving issues</li>
            </ul>

            <h3>3.2 User Authentication & Security</h3>
            <ul>
              <li>Verifying user identity and preventing fraud</li>
              <li>Securing accounts and protecting against unauthorized access</li>
              <li>Implementing security measures and monitoring platform integrity</li>
              <li>Detecting and preventing security breaches</li>
            </ul>

            <h3>3.3 Communication</h3>
            <ul>
              <li>Sending transactional emails (confirmations, receipts, notifications)</li>
              <li>Responding to your inquiries and support requests</li>
              <li>Providing updates about your appointments and consultations</li>
            </ul>

            <h3>3.4 Platform Improvement</h3>
            <ul>
              <li>Analyzing usage patterns to improve service quality</li>
              <li>Developing new features and functionality</li>
              <li>Conducting research and generating analytics</li>
              <li>Personalizing user experience</li>
            </ul>

            <h3>3.5 Legal & Compliance</h3>
            <ul>
              <li>Complying with legal obligations and regulations (HIPAA, GDPR, etc.)</li>
              <li>Enforcing our Terms of Service and other agreements</li>
              <li>Protecting rights, safety, and property of our users</li>
            </ul>

            <h3>3.6 Legitimate Business Purposes</h3>
            <ul>
              <li>Business analytics and performance metrics</li>
              <li>Platform optimization and troubleshooting</li>
              <li>Marketing and promotional activities (with consent)</li>
            </ul>
          </section>

          {/* Data Sharing */}
          <section className="privacy-section">
            <h2>4. How We Share Your Information</h2>
            <p>We may share your information in the following circumstances:</p>
            
            <h3>4.1 Service Providers</h3>
            <p>We share information with trusted third-party service providers who assist us in operating our platform, including:</p>
            <ul>
              <li>Payment processors and financial institutions</li>
              <li>Cloud hosting and storage providers</li>
              <li>Email and communication service providers</li>
              <li>Analytics providers</li>
            </ul>

            <h3>4.2 Healthcare Professionals</h3>
            <p>Medical records and consultation information are shared with healthcare providers as necessary for your care, with appropriate authorization.</p>

            <h3>4.3 Legal Requirements</h3>
            <p>We may disclose information when required by law or to comply with legal proceedings, court orders, or government requests.</p>

            <h3>4.4 Business Transfers</h3>
            <p>If we merge, acquire, or sell our business, your information may be transferred as part of that transaction.</p>

            <h3>4.5 With Your Consent</h3>
            <p>We may share information for other purposes with your explicit consent.</p>
          </section>

          {/* Data Security */}
          <section className="privacy-section">
            <h2>5. How We Protect Your Information</h2>
            <p>We implement comprehensive security measures to protect your information:</p>
            <ul>
              <li><strong>Encryption:</strong> Data transmitted over HTTPS with TLS encryption</li>
              <li><strong>Authentication:</strong> Secure password requirements and multi-factor authentication options</li>
              <li><strong>Access Controls:</strong> Role-based access restrictions for sensitive medical data</li>
              <li><strong>Regular Audits:</strong> Security assessments and vulnerability testing</li>
              <li><strong>Employee Training:</strong> Staff trained in data protection and privacy practices</li>
              <li><strong>Incident Response:</strong> Procedures for responding to data breaches</li>
              <li><strong>Compliance:</strong> HIPAA, GDPR, and other healthcare data protection standards</li>
            </ul>
            <p className="security-note">
              <strong>Note:</strong> While Doctaura implements robust security measures and is designed to be compliant with healthcare data protection standards, no system is completely secure. We cannot guarantee absolute security of your information.
            </p>
          </section>

          {/* Data Retention */}
          <section className="privacy-section">
            <h2>6. How Long We Retain Your Information</h2>
            <ul>
              <li><strong>Active Accounts:</strong> Information retained while your account is active</li>
              <li><strong>Medical Records:</strong> Retained per applicable healthcare regulations (typically 6-10 years after last visit)</li>
              <li><strong>Inactive Accounts:</strong> May retain data for compliance and legal purposes</li>
              <li><strong>Log Data:</strong> Typically retained for 12-24 months</li>
              <li><strong>Upon Deletion Request:</strong> Data deleted as specified in your deletion request, subject to legal retention requirements</li>
            </ul>
          </section>

          {/* User Rights & Data Deletion */}
          <section className="privacy-section">
            <h2>7. Your Privacy Rights & How to Request Data Deletion</h2>
            <p>You have the following rights regarding your personal information:</p>
            
            <h3>7.1 Right to Access</h3>
            <p>You can request a copy of all personal information we hold about you.</p>

            <h3>7.2 Right to Correction</h3>
            <p>You can request that we correct any inaccurate or incomplete information.</p>

            <h3>7.3 Right to Deletion (Right to be Forgotten)</h3>
            <p>You can request deletion of your personal data, subject to legal retention requirements and ongoing healthcare obligations.</p>

            <h3>7.4 Right to Opt-Out</h3>
            <p>You can opt-out of marketing communications and non-essential data processing.</p>

            <h3>7.5 How to Submit a Data Deletion Request</h3>
            <div className="deletion-request-box">
              <p><strong>To request deletion of your data, please contact us using any of the following methods:</strong></p>
              
              <div className="contact-method">
                <h4>📧 Email:</h4>
                <p>
                  <a href="mailto:doctauranet@gmail.com">doctauranet@gmail.com</a>
                </p>
                <p className="contact-detail">Subject line: "Data Deletion Request"</p>
              </div>

              <div className="contact-method">
                <h4>📞 Phone:</h4>
                <p>
                  <a href="tel:+230-5-807-33-15">+230 5 807 33 15</a>
                </p>
                <p className="contact-detail">Ask for our Privacy & Compliance Team</p>
              </div>

              <div className="contact-method">
                <h4>✉️ Mail:</h4>
                <p>
                  Doctaura Privacy Team<br/>
                  Robinson No1, Phoenix<br/>
                  73546<br/>
                  Mauritius
                </p>
              </div>

              <h4 className="deletion-process-title">Deletion Request Process:</h4>
              <ol className="deletion-process">
                <li><strong>Submit Request:</strong> Send your deletion request with your name, email, and account details</li>
                <li><strong>Verification:</strong> We will verify your identity to confirm you are the account holder (typically within 3-5 business days)</li>
                <li><strong>Processing:</strong> Once verified, we will process your request and delete your data (typically within 30 days)</li>
                <li><strong>Confirmation:</strong> You will receive a confirmation email when your data has been deleted</li>
                <li><strong>Legal Holds:</strong> Some data may be retained if required by law or for medical record compliance</li>
              </ol>

              <p className="deletion-note">
                <strong>Important Note:</strong> Deletion requests may take up to 30 days to process. Some information may be retained longer if required by legal obligations or medical record retention laws.
              </p>
            </div>
          </section>

          {/* Children's Privacy */}
          <section className="privacy-section">
            <h2>8. Children's Privacy</h2>
            <p>
              Our platform is not intended for users under 18 years old. We do not knowingly collect information from children under 18. If we become aware that a child under 18 has provided us with information, we will promptly delete such information.
            </p>
            <p>
              Parents or guardians who believe their child has provided information to our platform should contact us immediately at <a href="mailto:doctauranet@gmail.com">doctauranet@gmail.com</a>.
            </p>
          </section>

          {/* Third-Party Links */}
          <section className="privacy-section">
            <h2>9. Third-Party Links & Services</h2>
            <p>
              Our platform may contain links to third-party websites and services. This Privacy Policy applies only to our platform. We are not responsible for the privacy practices of third-party websites. We encourage you to review their privacy policies before providing any information.
            </p>
          </section>

          {/* Cookie Policy */}
          <section className="privacy-section">
            <h2>10. Cookies & Tracking Technologies</h2>
            <p>We use cookies and similar tracking technologies to:</p>
            <ul>
              <li>Remember your preferences and login information</li>
              <li>Analyze usage patterns and improve our platform</li>
              <li>Enable essential platform functionality</li>
              <li>Provide personalized content and recommendations</li>
            </ul>
            <p>You can control cookie preferences through your browser settings. Disabling cookies may affect platform functionality.</p>
          </section>

          {/* International Data Transfers */}
          <section className="privacy-section">
            <h2>11. International Data Transfers</h2>
            <p>
              While Doctaura Health Management System is designed for use in Mauritius, your information may be transferred to, stored in, and processed in countries other than Mauritius for cloud hosting, backup, and service delivery purposes. These countries may have different data protection laws. By using our platform, you consent to the transfer of your information to countries outside Mauritius if necessary for service provision.
            </p>
            <p>
              <strong>Note:</strong> Users outside Mauritius should be aware that this system is not designed for international use and may not comply with data protection laws in other jurisdictions.
            </p>
          </section>

          {/* Policy Updates */}
          <section className="privacy-section">
            <h2>12. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of material changes by posting the updated policy on our website with a new effective date.
            </p>
            <p>
              Your continued use of our platform following the posting of changes constitutes your acceptance of such changes.
            </p>
          </section>

          {/* Contact Information */}
          <section className="privacy-section contact-section">
            <h2>13. Contact Us</h2>
            <p>If you have questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us:</p>
            
            <div className="contact-info">
              <div className="contact-item">
                <strong>Privacy & Compliance Team</strong>
                <p>Email: <a href="mailto:doctauranet@gmail.com">doctauranet@gmail.com</a></p>
                <p>Phone: <a href="tel:+230-5-807-33-15">+230 5 807 33 15</a></p>
              </div>
            </div>

            <p className="response-time">
              We aim to respond to all privacy inquiries within 7-10 business days.
            </p>
          </section>

          {/* Compliance Statement */}
          <section className="privacy-section compliance-section">
            <h2>14. Regulatory Compliance</h2>
            <p>
              Doctaura Health Management System is designed to comply with applicable data protection and privacy laws and regulations in Mauritius and internationally, including:
            </p>
            <ul>
              <li><strong>Mauritius Data Protection Act:</strong> We are committed to complying with Mauritius data protection regulations and privacy laws</li>
              <li><strong>HIPAA (Health Insurance Portability and Accountability Act):</strong> We aim to protect health information in accordance with HIPAA standards where applicable</li>
              <li><strong>GDPR (General Data Protection Regulation):</strong> We design our practices to be compliant with GDPR for users in the European Union</li>
              <li><strong>CCPA (California Consumer Privacy Act):</strong> We strive to comply with CCPA requirements for California residents</li>
              <li><strong>State Healthcare Privacy Laws:</strong> We are committed to complying with applicable privacy laws by jurisdiction</li>
            </ul>
          </section>

          {/* Footer */}
          <section className="privacy-footer">
            <p>
              <strong>Effective Date:</strong> 21 September 2026
            </p>
            <p>
              This Privacy Policy is maintained in English. If there is a conflict between this English version and any translation, the English version shall prevail.
            </p>
          </section>
        </div>

        <div className="privacy-actions">
          <a href="/login" className="login-link">Return to Login</a>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
